import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BiPlus, BiPencil, BiTrash } from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls.js";
import axiosClient from '../utils/apiClient.js';


const getInitialsPlaceholder = (firstName, lastName) => {
  const fullName = `${firstName || ""} ${lastName || ""}`;
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(
    fullName
  )}&background=random&color=fff&size=40&bold=true`;
};

const TeamLimitNote = ({ userLimit }) => {
  const displayLimit =
    userLimit === null || typeof userLimit === "undefined"
      ? "Unlimited"
      : userLimit === 1
        ? "1"
        : userLimit;

  const noteText =
    userLimit === null || typeof userLimit === "undefined"
      ? "Team Member Limit : Unlimited"
      : userLimit === 1
        ? "Team Member Limit: 1 (Full)"
        : `Team Member Limit: ${displayLimit}`;

  return (
    <div className="p-3 rounded-md bg-yellow-100 border border-yellow-200 text-yellow-800 text-sm sm:text-base mt-2 sm:mt-0">
      {noteText}
    </div>
  );
};

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const userLimit = user?.organization?.userLimit;

  const formatLabel = (value) => {
    if (!value) return "";
    return value
      .toString()
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };


  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axiosClient.get(API_BASE_URL + "/teams");
        setUsers(response.data);
      } catch (err) {
        setError("Failed to fetch users. Please try again.");
        console.error("Error fetching users:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(
    (user) =>
      `${user.first_name} ${user.last_name}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.role_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    setSelectedIds([]);
  }, [searchQuery]);

  const handleToggleSelect = (userId) => {
    setSelectedIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (filteredUsers.length > 0 && selectedIds.length === filteredUsers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredUsers.map((u) => u.id));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (
      !window.confirm(
        `Are you sure you want to delete the ${selectedIds.length} selected team member(s)?`
      )
    )
      return;

    setIsDeleting(true);
    setError(null);
    try {
      await axiosClient.post(`${API_BASE_URL}/users/bulk-delete`, {
        userIds: selectedIds,
      });
      setUsers((prev) => prev.filter((u) => !selectedIds.includes(u.id)));
      setSelectedIds([]);
    } catch (err) {
      console.error("Bulk delete error, trying fallback:", err);
      try {
        await Promise.all(
          selectedIds.map((id) => axiosClient.delete(`${API_BASE_URL}/users/${id}`))
        );
        setUsers((prev) => prev.filter((u) => !selectedIds.includes(u.id)));
        setSelectedIds([]);
      } catch (fallbackErr) {
        setError(err.response?.data?.message || "Failed to delete selected team members.");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axiosClient.delete(`${API_BASE_URL}/users/${userId}`);
        setUsers(users.filter((user) => user.id !== userId));
        setSelectedIds((prev) => prev.filter((id) => id !== userId));
      } catch (err) {
        setError("Failed to delete user.");
        console.error("Error deleting user:", err);
      }
    }
  };

  if (loading) return <div className="text-center text-gray-500 p-6">Loading...</div>;
  if (error) return <div className="text-center text-red-500 p-6">{error}</div>;

  return (
    <div className="container mx-auto p-1 md:p-4">
      {/* Header section */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Teams</h1>
        <TeamLimitNote userLimit={userLimit} />
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full sm:w-64 px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Link
            to="/teams/new"
            className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition"
          >
            <BiPlus className="mr-2" /> Add New
          </Link>
        </div>
      </div>

      {/* Bulk actions banner */}
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 transition-all">
          <div className="flex items-center space-x-2 text-sm text-blue-800 font-medium">
            <span>{selectedIds.length} of {filteredUsers.length} selected</span>
            <button
              onClick={() => setSelectedIds([])}
              className="text-xs text-blue-600 underline hover:text-blue-800 ml-2"
            >
              Clear Selection
            </button>
          </div>
          <button
            onClick={handleBulkDelete}
            disabled={isDeleting}
            className="flex items-center px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition disabled:opacity-50"
          >
            <BiTrash className="mr-1 h-4 w-4" />
            {isDeleting ? "Deleting..." : `Delete Selected (${selectedIds.length})`}
          </button>
        </div>
      )}

      {/* Table for large screens */}
      <div className="hidden md:block bg-white shadow-lg rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left w-10">
                <input
                  type="checkbox"
                  checked={filteredUsers.length > 0 && selectedIds.length === filteredUsers.length}
                  onChange={handleSelectAll}
                  aria-label="Select all team members"
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Role
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <tr
                  key={user.id}
                  className={`hover:bg-gray-50 transition-colors ${
                    selectedIds.includes(user.id) ? "bg-blue-50/50" : ""
                  }`}
                >
                  <td className="px-4 py-4 text-sm w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(user.id)}
                      onChange={() => handleToggleSelect(user.id)}
                      aria-label={`Select ${user.first_name} ${user.last_name}`}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap flex items-center">
                    <img
                      src={
                        user.profile_image ||
                        getInitialsPlaceholder(user.first_name, user.last_name)
                      }
                      alt={`${user.first_name}'s profile`}
                      className="h-10 w-10 rounded-full object-cover mr-3"
                    />
                    <span className="text-sm font-medium text-gray-900">
                      {user.first_name} {user.last_name}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatLabel(user.role_name)}
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium space-x-3">
                    <Link
                      to={`/teams/${user.id}`}
                      className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                    >
                      <BiPencil className="mr-1" /> Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="text-red-600 hover:text-red-900 inline-flex items-center"
                    >
                      <BiTrash className="mr-1" /> Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-4 text-sm text-gray-500">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {/* Card layout for mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden">
        {filteredUsers.length > 0 ? (
          filteredUsers.map((user) => (
            <div
              key={user.id}
              className={`bg-white rounded-lg shadow-md p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between ${
                selectedIds.includes(user.id) ? "ring-2 ring-blue-500 bg-blue-50/30" : ""
              }`}
            >
              <div className="flex items-center mb-3 sm:mb-0">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(user.id)}
                  onChange={() => handleToggleSelect(user.id)}
                  aria-label={`Select ${user.first_name} ${user.last_name}`}
                  className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer mr-3"
                />
                <img
                  src={
                    user.profile_image ||
                    getInitialsPlaceholder(user.first_name, user.last_name)
                  }
                  alt={`${user.first_name}'s profile`}
                  className="h-12 w-12 rounded-full object-cover mr-3"
                />
                <div>
                  <h2 className="font-semibold text-gray-800">
                    {user.first_name} {user.last_name}
                  </h2>
                  <p className="text-sm text-gray-500">{user.email}</p>
                  <p className="text-sm text-gray-500">{formatLabel(user.role_name)}</p>
                </div>
              </div>
              <div className="flex gap-3 mt-2 sm:mt-0">
                <Link
                  to={`/teams/${user.id}`}
                  className="text-blue-600 hover:text-blue-800 inline-flex items-center text-sm"
                >
                  <BiPencil className="mr-1" /> Edit
                </Link>
                <button
                  onClick={() => handleDelete(user.id)}
                  className="text-red-600 hover:text-red-800 inline-flex items-center text-sm"
                >
                  <BiTrash className="mr-1" /> Delete
                </button>
              </div>
            </div>
          ))

        ) : (
          <div className="text-center text-gray-500 py-4 col-span-full">
            No users found.
          </div>
        )}
      </div>
    </div>
  );
};

export default UserList;
